/**
 * AVBP Intake FORMS PICKER — pure helpers for the "Client OR Project" launcher
 * (Increment B UI, AD-030, Anil).
 *
 * The standalone Forms launcher (when an intake is NOT opened from a project
 * context) lets staff pick a CLIENT and then, if that client has more than one
 * project, REQUIRES choosing the project the intake belongs to (so a project-
 * scoped intake is never filed against the wrong project).
 *
 * Firebase-free + React-free so the `.cjs` runner exercises the project-listing
 * + selection-required logic.
 */

/** The minimal project row this picker needs (a structural subset of LiveProject). */
export interface PickerProject {
  /** VP### — the project id. */
  vp: string;
  /** authoritative client link (C##### / VP key). */
  clientId: string;
  /** display: client number cache. */
  clientNo?: string;
  /** display: "tier · name". */
  tier?: string;
  /** raw tier code (L1A / L2B …). */
  tierCode?: string;
}

/**
 * PURE — the projects belonging to a client, by the authoritative `clientId`
 * link (with a `clientNo` fallback for any pre-IA-fix row). Newest VP first
 * (the caller's stream is already sorted, but we keep this stable + total here).
 */
export function projectsForClient(
  projects: PickerProject[],
  clientId: string,
): PickerProject[] {
  const cid = (clientId || '').trim();
  if (!cid) return [];
  const mine = projects.filter(
    (p) =>
      (p.clientId || '').trim() === cid || (p.clientNo || '').trim() === cid,
  );
  return mine.slice().sort((a, b) => vpNum(b.vp) - vpNum(a.vp));
}

/** PURE — VP### → integer for sorting. Malformed → 0. */
export function vpNum(vp: string): number {
  return parseInt((vp || '').replace(/^VP/i, ''), 10) || 0;
}

/**
 * PURE — the picker's resolution for a client selection:
 *   • projects        — the client's projects (newest first).
 *   • needsChoice      — the staff MUST pick a project (>1 project).
 *   • autoProjectId    — the single project to auto-scope to (exactly 1 project);
 *                        undefined when 0 (no project — client-scoped) or >1.
 * A client with 0 projects yields a client-scoped intake (autoProjectId
 * undefined, needsChoice false) — the universal Scoping form path (Increment C).
 */
export interface PickerResolution {
  projects: PickerProject[];
  needsChoice: boolean;
  autoProjectId?: string;
}

export function resolveFormsPicker(
  projects: PickerProject[],
  clientId: string,
): PickerResolution {
  const mine = projectsForClient(projects, clientId);
  if (mine.length === 1) {
    return { projects: mine, needsChoice: false, autoProjectId: mine[0].vp };
  }
  return { projects: mine, needsChoice: mine.length > 1 };
}

/** PURE — the dropdown label for a project option in the picker. */
export function projectOptionLabel(p: PickerProject): string {
  const tier = (p.tier || p.tierCode || '').trim();
  return tier ? `${p.vp} · ${tier}` : p.vp;
}
